import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {UntilDestroy} from '@ngneat/until-destroy';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {
  EntityTypes,
  KybAddedParties,
  KybEntityResult,
  KybInitiateCorporatePartyFn,
  KybManualPerson,
  KybPartyIdentifiers,
  KybPerson,
  KybPersonRoleSelection,
  KybRefreshFn,
  KybResult,
  KybResultFn,
  KybSignerEntityView,
  PartnerValue,
  TrusteeValue,
  kybAddedPartiesToPartyValue,
  kybDigitsOnly,
  kybDisplayStatus,
  kybFindEntityForParty,
  kybHasOwnKeyPeople,
  kybNormalizePartyDobForSave,
  kybPartyEntryIntegrityErrors,
  kybPartyIdentifiers,
  kybSectionHeading,
  kybSectionWarning,
  uniqueIntegrityMessages,
} from '@portal-workspace/grow-shared-library';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {MarkDirective} from '../../directives/mark-as-dirty.directive';
import {BusinessNumberSearchFn} from '../business-number-search-component/business-number-search.component';
import {PartnerComponent} from '../individual-and-entity-info-component/partner.component';
import {TrusteeComponent} from '../individual-and-entity-info-component/trustee.component';
import {MessageBoxComponent} from '../message-box/message-box.component';

type KybPartyControl = FormControl<PartnerValue | TrusteeValue | null>;

/** Everything the owning step needs after any change to a parent's parties or their KYB runs. */
export interface KybAddedPartiesEvent {
  /** This parent's group plus every nested parent's, in the shape the Key People save expects. */
  addedParties: KybAddedParties[];
  /** Resolved company parties still listed on screen, for the owner's key people validation. */
  corporateParties: KybEntityResult[];
  /** Stakeholders whose row has gone, so the owner drops the selections it holds for them. */
  removedStakeholderIds: number[];
  /** False while a listed party is incomplete or its own check has not landed. */
  valid: boolean;
  /** Why `valid` is false, when the cause has no field of its own to highlight. */
  blockingReason: string | null;
}

/**
 * The trustees/partners of one trust or partnership entity, and everything that hangs off them.
 *
 * Neither a trust nor a partnership has officers of its own for FrankieOne to return, so its key
 * people are the parties collected here instead. A party that is a company contributes its own
 * directors/shareholders as candidate signers, which is why each entity row starts its own KYB run;
 * a party that is itself a trust or partnership makes the same substitution one level down, in the
 * nested section rendered inside its own card.
 *
 * Owns the party rows and their runs but not the guarantor/signer ticks: those are bubbled up,
 * because the step they belong to holds them for every section at once.
 */
@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  selector: 'kyb-added-parties',
  templateUrl: './kyb-added-parties.component.html',
  styleUrls: ['./kyb-added-parties.component.scss'],
  providers: [
    {provide: MARK, useExisting: forwardRef(() => KybAddedPartiesComponent)},
  ],
  standalone: true,
  imports: [
    FormsModule, ReactiveFormsModule, MarkDirective, PartnerComponent, TrusteeComponent,
    MessageBoxComponent,
  ],
})
export class KybAddedPartiesComponent implements OnInit, OnChanges, Mark {

  @Input({required: true}) applicationId!: number;
  /** The trust/partnership whose parties are collected here. */
  @Input({required: true}) parentEntity!: KybEntityResult;
  @Input({required: true}) entityType!: EntityTypes;
  @Input({required: true}) businessNumberSearchFn!: BusinessNumberSearchFn;
  // The read model, owned by the step so every section agrees on one copy of it. Re-supplied after
  // each poll; the party forms are never rebuilt from it, or a poll would discard what is being typed.
  @Input({required: false}) kybResult: KybResult | null = null;
  @Input({required: false}) kybResultFn: KybResultFn | null = null;
  // Backs the Retry inside a party's waiting panel: pulls the execution from FrankieOne rather than
  // starting a second, billable one.
  @Input({required: false}) kybRefreshFn: KybRefreshFn | null = null;
  @Input({required: false}) kybInitiateCorporatePartyFn: KybInitiateCorporatePartyFn | null = null;
  @Input({required: false}) showAssetsAndLiabilities = false;
  // Handed to each party's signers table: see kyb-entity-section.
  @Input({required: false}) guarantorLockedStakeholderIds: ReadonlySet<number> = new Set<number>();

  @Output() changed = new EventEmitter<KybAddedPartiesEvent>();
  @Output() personRoleChange = new EventEmitter<KybPersonRoleSelection>();
  @Output() personSelectedChange = new EventEmitter<{ person: KybPerson; selected: boolean }>();
  /** A party's own check landed inside its waiting panel and handed back the refreshed read model. */
  @Output() resolved = new EventEmitter<KybResult>();
  @Output() manualKybEntry = new EventEmitter<number>();
  @Output() manualPeopleChange = new EventEmitter<{ commercialEntityId: number; people: KybManualPerson[] }>();
  @Output() manualValidityChange = new EventEmitter<{ commercialEntityId: number; valid: boolean }>();

  subscriptions: Subscription[] = [];
  markObservable: Subject<boolean> = new Subject<boolean>();

  kybSectionHeading = kybSectionHeading;
  kybSectionWarning = kybSectionWarning;

  partyControl!: KybPartyControl;

  // Add-party controls for parties that are themselves a trust/partnership, keyed by
  // commercialEntityId (kybResultId is null until the run completes, so it is not a stable key).
  private nestedControls: Map<number, { entity: KybEntityResult; control: KybPartyControl }> = new Map();
  // Latest rows per parent commercialEntityId, valid or not. Tells a party still on screen from one
  // left behind by a removed or replaced row.
  private partyEntries: Map<number, readonly unknown[]> = new Map();
  // Parties still listed in a row, mapped to the entity type that row declared. The row's own
  // ABN/ACN lookup is the authority: FrankieOne is never asked about a trust or partnership, so its
  // run carries no type and the entity's own column cannot decide which parties to collect.
  private corporateParties: Map<number, EntityTypes | null> = new Map();
  // Parties already sent to the initiate endpoint, so retyping an ACN does not re-fire.
  private requestedPartyKeys: Set<string> = new Set<string>();
  // Per-parent signer lookups, memoised so the child inputs keep a stable identity.
  private signerLookups: Map<number, (row: FormGroup) => KybSignerEntityView | null> = new Map();
  private manualValidityByEntity = new Map<number, boolean>();

  private blockingReason: string | null = null;
  // Distinct integrity errors of the rows on screen, shown as soon as a row is entered (see
  // KybKeyPeopleComponent.partyIntegrityErrors).
  integrityErrors: string[] = [];

  ngOnInit(): void {
    setupUntilDestroy(this);
    // Seeded at construction rather than set afterwards: the party forms build a row per value
    // written to them, so a second write would duplicate every restored party.
    this.partyControl = this.newPartyControl(this.parentEntity);
    this.seedRequestedKeys(this.parentEntity, this.roleFor(this.entityType));
    this.refreshCorporateParties();
    this.rebuildNestedControls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kybResult'] && !changes['kybResult'].firstChange) {
      // The result moved on (a poll landed, or a party's run completed): re-resolve which parties
      // are on screen and which of them now need a nested section. The forms are left alone.
      this.refreshCorporateParties();
      this.rebuildNestedControls();
      // Deferred because this runs inside the owning step's change detection pass, and the step
      // re-validates itself from what is emitted; doing that synchronously would change bindings it
      // has already rendered.
      queueMicrotask(() => this.emit());
    }
  }

  private roleFor(entityType: EntityTypes): 'partner' | 'trustee' {
    return entityType === 'trust' ? 'trustee' : 'partner';
  }

  private newPartyControl(entity: KybEntityResult): KybPartyControl {
    const control: KybPartyControl = new FormControl<PartnerValue | TrusteeValue | null>(
      this.seededPartyValue(entity),
    );
    this.subscriptions.push(control.valueChanges.pipe(tap(() => this.emit())).subscribe());
    return control;
  }

  // Rows a restored form starts from; null leaves a first-time form on its own defaults (two blank
  // partners / one blank trustee).
  private seededPartyValue(entity: KybEntityResult): PartnerValue | TrusteeValue {
    const group = (this.kybResult?.addedParties ?? [])
      .find(candidate => candidate.commercialEntityId === entity.commercialEntityId);
    if (!group?.parties?.length) {
      return null;
    }
    const parties = kybAddedPartiesToPartyValue(group.parties, this.effectiveEntityType(entity));
    if (parties?.length) {
      this.partyEntries.set(entity.commercialEntityId, parties);
    }
    return parties;
  }

  /**
   * Suppress the initiate call the restored rows would otherwise fire on load, for parties that
   * already have a run. A row whose run is missing is deliberately left out, so it still gets one
   * rather than sitting there with no key people forever.
   */
  private seedRequestedKeys(parent: KybEntityResult, role: 'partner' | 'trustee'): void {
    for (const party of this.partyEntries.get(parent.commercialEntityId) ?? []) {
      if (!this.entityForPartyRow(parent.commercialEntityId, party)) {
        continue;
      }
      const {abn, acn} = kybPartyIdentifiers(party);
      this.requestedPartyKeys.add(`${parent.commercialEntityId}|${role}|${abn ?? ''}|${acn ?? ''}`);
    }
  }

  effectiveEntityType(entity: KybEntityResult): EntityTypes {
    if (entity.commercialEntityId === this.parentEntity.commercialEntityId) {
      return this.entityType;
    }
    return this.corporateParties.get(entity.commercialEntityId) ?? entity.entityType;
  }

  /**
   * Display status of one party. A trust or partnership is resolved on its type alone: its key
   * people are the parties entered against it, so its own run holds nothing to wait for.
   */
  statusFor(entity: KybEntityResult) {
    if (entity.mode === 'MANUAL') {
      return 'resolved';
    }
    return kybHasOwnKeyPeople(this.effectiveEntityType(entity))
      ? kybDisplayStatus(entity.status)
      : 'resolved';
  }

  onManualKybEntry(commercialEntityId: number): void {
    this.manualValidityByEntity.set(commercialEntityId, false);
    this.manualKybEntry.emit(commercialEntityId);
    this.emit();
  }

  onManualPeople(event: { commercialEntityId: number; people: KybManualPerson[] }): void {
    const entity = this.kybResult?.entities.find(
      candidate => candidate.commercialEntityId === event.commercialEntityId,
    );
    if (entity) entity.manualPeople = event.people;
    this.manualPeopleChange.emit(event);
  }

  onManualValidity(event: { commercialEntityId: number; valid: boolean }): void {
    this.manualValidityByEntity.set(event.commercialEntityId, event.valid);
    this.manualValidityChange.emit(event);
    this.emit();
  }

  nestedControlFor(entity: KybEntityResult): KybPartyControl | null {
    return this.nestedControls.get(entity.commercialEntityId)?.control ?? null;
  }

  signerEntityLookupFor(parent: KybEntityResult): (row: FormGroup) => KybSignerEntityView | null {
    let lookup = this.signerLookups.get(parent.commercialEntityId);
    if (!lookup) {
      lookup = (row: FormGroup) => this.resolveSignerEntity(parent, row);
      this.signerLookups.set(parent.commercialEntityId, lookup);
    }
    return lookup;
  }

  // Match a party row to the KYB entity created for it, so its signers table shows that company's
  // own officers rather than the parent's. The status rides along: the entity is matchable from the
  // moment its run is initiated, so without it the table would read an in-flight run as one that
  // returned nobody.
  private resolveSignerEntity(parent: KybEntityResult, row: FormGroup): KybSignerEntityView | null {
    const entity = kybFindEntityForParty(
      this.kybResult?.entities ?? [],
      parent.commercialEntityId,
      this.rowIdentifiers(row),
    );
    return entity && this.kybResultFn
      ? {
        entity,
        status: this.statusFor(entity),
        applicationId: this.applicationId,
        pollFn: this.kybResultFn,
        refreshFn: this.kybRefreshFn,
      }
      : null;
  }

  private rowIdentifiers(row: FormGroup) {
    return kybPartyIdentifiers({
      manualEntry: !!row.get('manualEntry')?.value,
      stepacn: row.get('stepacn')?.value ?? undefined,
      organisation: row.get('organisation')?.value ?? undefined,
    });
  }

  private entityForPartyRow(parentCommercialEntityId: number, entry: unknown): KybEntityResult | null {
    return kybFindEntityForParty(
      this.kybResult?.entities ?? [],
      parentCommercialEntityId,
      kybPartyIdentifiers(entry),
    );
  }

  /**
   * The party behind one row, when that party is itself a trust or a partnership. Such a row gets
   * its own trustee/partner section rather than a signers table. Resolved per row rather than per
   * parent so the section sits inside the card it belongs to, and disappears with that row.
   */
  nestedPartyForRow(parent: KybEntityResult, row: FormGroup): KybEntityResult | null {
    const entity = this.resolveSignerEntity(parent, row)?.entity ?? null;
    if (!entity) {
      return null;
    }
    return kybHasOwnKeyPeople(this.effectiveEntityType(entity)) ? null : entity;
  }

  /**
   * A party list changed: start a KYB run for any entity row whose ACN/ABN is now usable. Tracked
   * by identifier so editing an ACN does not fire repeatedly; the endpoint is idempotent as a
   * second line of defence.
   */
  onPartyEvent(parent: KybEntityResult, role: 'partner' | 'trustee', event: { entries: unknown[] }): void {
    this.partyEntries.set(parent.commercialEntityId, event.entries ?? []);
    const removedStakeholderIds = this.removedStakeholderIdsFor(parent, role, event.entries ?? []);
    // The rows decide which parties are still on screen, which is what makes a trust party's nested
    // section appear (or disappear with the row that introduced it).
    this.refreshCorporateParties();
    this.rebuildNestedControls();
    this.emit(removedStakeholderIds);
    if (!this.kybInitiateCorporatePartyFn) {
      return;
    }
    for (const entry of event.entries ?? []) {
      const party = entry as {
        type?: string;
        manualEntry?: boolean;
        businessName?: string;
        organisation?: { name?: string };
        organisationType?: { type?: string };
      };
      if (party?.type !== 'Entity') {
        continue;
      }
      const {abn, acn} = kybPartyIdentifiers(entry);
      if (!abn && !acn) {
        continue;
      }
      // An invalid entity is never saved nor sent to FrankieOne; the row is flagged instead.
      if (kybPartyEntryIntegrityErrors(entry, role, this.primaryIdentifiers()).length) {
        continue;
      }
      const key = `${parent.commercialEntityId}|${role}|${abn ?? ''}|${acn ?? ''}`;
      if (this.requestedPartyKeys.has(key)) {
        continue;
      }
      // Marked before the call so a burst of row events cannot fire twice, then released on failure:
      // initiation here is the only thing that creates the run, and a panel's Retry only pulls an
      // existing execution, so a key left behind would strand the row with no key people forever.
      this.requestedPartyKeys.add(key);
      this.subscriptions.push(
        this.kybInitiateCorporatePartyFn({
          applicationId: this.applicationId,
          parentCommercialEntityId: parent.commercialEntityId,
          role,
          legalName: (party.manualEntry ? party.businessName : party.organisation?.name) ?? null,
          abn,
          acn,
          entityType: party.organisationType?.type ?? null,
        }).pipe(
          tap(() => this.reload()),
          catchError(() => {
            this.requestedPartyKeys.delete(key);
            return EMPTY;
          }),
        ).subscribe(),
      );
    }
  }

  // The run now exists, so hand the refreshed read model to the step, which re-supplies it to every
  // section at once rather than each holding its own copy. Also backs the Retry a party row offers
  // while its own entity is still missing from the model.
  reload(): void {
    if (!this.kybResultFn) {
      return;
    }
    this.subscriptions.push(this.kybResultFn(this.applicationId).pipe(
      tap(result => this.resolved.emit(result)),
      catchError(() => EMPTY),
    ).subscribe());
  }

  /**
   * Stakeholders belonging to a row that has gone. Without dropping their selections the save
   * writes individual roles for an entity it soft-deletes in the same transaction.
   */
  private removedStakeholderIdsFor(
    parent: KybEntityResult,
    role: 'partner' | 'trustee',
    entries: readonly unknown[],
  ): number[] {
    const live = new Set<string>();
    for (const entry of entries) {
      const {abn, acn} = kybPartyIdentifiers(entry);
      if (abn) live.add(`abn:${abn}`);
      if (acn) live.add(`acn:${acn}`);
    }
    const removed = (this.kybResult?.entities ?? []).filter(entity =>
      entity.parentCommercialEntityId === parent.commercialEntityId
      && (role === 'partner' ? entity.isPartnerOfParent : entity.isTrusteeOfParent)
      && !(entity.abn && live.has(`abn:${kybDigitsOnly(entity.abn)}`))
      && !(entity.acn && live.has(`acn:${kybDigitsOnly(entity.acn)}`)),
    );
    if (!removed.length) {
      return [];
    }
    // A removed trust party takes its nested section with it, so the companies that section
    // introduced are orphaned too and must go alongside their parent.
    const orphaned = [...removed];
    for (const entity of removed) {
      this.partyEntries.delete(entity.commercialEntityId);
      this.nestedControls.delete(entity.commercialEntityId);
      orphaned.push(...(this.kybResult?.entities ?? []).filter(
        candidate => candidate.parentCommercialEntityId === entity.commercialEntityId,
      ));
    }
    return orphaned.flatMap(entity => entity.people.map(person => person.stakeholderId));
  }

  /**
   * Parties still listed in a row, each mapped to the entity type its row declared. An entity keeps
   * its KYB run until the step is saved, so a company the user removed or replaced is still in the
   * result. Those must not gate the step: they are rendered nowhere, so blocking on them leaves the
   * user with no way to see or fix the problem.
   */
  private refreshCorporateParties(): void {
    const parties = new Map<number, EntityTypes | null>();
    for (const [parentCommercialEntityId, entries] of this.partyEntries) {
      for (const entry of entries) {
        const entity = this.entityForPartyRow(parentCommercialEntityId, entry);
        if (entity) {
          parties.set(entity.commercialEntityId, this.partyEntityType(entry));
        }
      }
    }
    this.corporateParties = parties;
  }

  private partyEntityType(entry: unknown): EntityTypes | null {
    return (entry as { organisationType?: { type?: EntityTypes } } | null)?.organisationType?.type ?? null;
  }

  // A party that is itself a trust/partnership needs its own add-party control, but only while it
  // is still listed: one left behind by a removed row renders nowhere, so a control for it could
  // never be filled in and would block the step.
  private rebuildNestedControls(): void {
    const next: Map<number, { entity: KybEntityResult; control: KybPartyControl }> = new Map();
    for (const entity of this.kybResult?.entities ?? []) {
      if (!this.corporateParties.has(entity.commercialEntityId)) {
        continue;
      }
      if (kybHasOwnKeyPeople(this.effectiveEntityType(entity))) {
        continue;
      }
      const existing = this.nestedControls.get(entity.commercialEntityId);
      if (existing) {
        next.set(entity.commercialEntityId, existing);
        continue;
      }
      const control = this.newPartyControl(entity);
      this.seedRequestedKeys(entity, this.roleFor(this.effectiveEntityType(entity)));
      next.set(entity.commercialEntityId, {entity, control});
    }
    this.nestedControls = next;
  }

  // Only a party whose own key people come from FrankieOne can gate the step. A trust or
  // partnership party has nothing to wait for, so the state of its run must not block.
  private pendingParties(): KybEntityResult[] {
    return (this.kybResult?.entities ?? []).filter(entity =>
      this.corporateParties.has(entity.commercialEntityId)
      && kybHasOwnKeyPeople(this.effectiveEntityType(entity))
      && this.statusFor(entity) !== 'resolved');
  }

  /** Resolved company parties still listed, which the owning step holds to the company rules. */
  private resolvedCompanyParties(): KybEntityResult[] {
    return (this.kybResult?.entities ?? []).filter(entity =>
      this.corporateParties.has(entity.commercialEntityId)
      && this.effectiveEntityType(entity) === 'company'
      && this.statusFor(entity) === 'resolved');
  }

  private buildAddedParties(): KybAddedParties[] {
    const groups: KybAddedParties[] = [{
      commercialEntityId: this.parentEntity.commercialEntityId,
      entityType: this.entityType,
      parties: kybNormalizePartyDobForSave(this.partyControl.value),
    }];
    for (const {entity, control} of this.nestedControls.values()) {
      groups.push({
        commercialEntityId: entity.commercialEntityId,
        entityType: this.effectiveEntityType(entity),
        parties: kybNormalizePartyDobForSave(control.value),
      });
    }
    return groups;
  }

  private primaryIdentifiers(): KybPartyIdentifiers | null {
    const primary = this.kybResult?.entities.find(entity => entity.isPrimary);
    return primary ? {abn: primary.abn, acn: primary.acn} : null;
  }

  private collectIntegrityErrors(): string[] {
    const primary = this.primaryIdentifiers();
    const errors: { message: string }[] = [];
    const parents = [
      this.parentEntity,
      ...Array.from(this.nestedControls.values()).map(({entity}) => entity),
    ];
    for (const parent of parents) {
      const role = this.roleFor(this.effectiveEntityType(parent));
      for (const entry of this.partyEntries.get(parent.commercialEntityId) ?? []) {
        errors.push(...kybPartyEntryIntegrityErrors(entry, role, primary));
      }
    }
    return uniqueIntegrityMessages(errors);
  }

  private emit(removedStakeholderIds: number[] = []): void {
    this.blockingReason = null;
    this.integrityErrors = this.collectIntegrityErrors();

    const pending = this.pendingParties();
    const invalidManual = this.resolvedCompanyParties().some(
      entity => entity.mode === 'MANUAL' && !this.manualValidityByEntity.get(entity.commercialEntityId),
    );
    if (this.integrityErrors.length) {
      this.blockingReason = this.integrityErrors.join(' ');
    } else if (invalidManual) {
      this.blockingReason = 'Complete every manually entered person and select at least one signer before continuing.';
    } else if (pending.length) {
      const names = pending.map(entity => (entity.entityName ?? '').trim() || 'a company you added').join(', ');
      this.blockingReason = `We are still confirming the key people for ${names}. ` +
        `Open "Select signers" for that party to see its progress, then continue once it completes.`;
    } else if (this.partyControlsInvalid()) {
      // A party control is null while its own form is invalid, so an incomplete list must not pass
      // the step - without this, Validators.required is satisfied by the wrapper value.
      this.blockingReason = 'Complete the details of every party listed above before continuing.';
    }

    this.changed.emit({
      addedParties: this.buildAddedParties(),
      corporateParties: this.resolvedCompanyParties(),
      removedStakeholderIds,
      valid: !this.blockingReason,
      blockingReason: this.blockingReason,
    });
  }

  private partyControlsInvalid(): boolean {
    if (this.partyControl.invalid || this.partyControl.value == null) {
      return true;
    }
    return Array.from(this.nestedControls.values())
      .some(({control}) => control.invalid || control.value == null);
  }

  nestedParties(): { entity: KybEntityResult; control: KybPartyControl }[] {
    return Array.from(this.nestedControls.values());
  }

  mark(): void {
    this.markObservable.next(true);
  }
}
